/**
 * Single source of truth for how document states are named and colored in the UI.
 * Supplier-facing and reviewer-facing screens should both read from here so the
 * same state never shows two different labels or tones.
 */
export type StatusTone = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

export type DisplayStatus =
  | 'approved'
  | 'pending_approval'
  | 'correction_requested'
  | 'rejected'
  | 'overdue'
  | 'missing'
  | 'expired'
  | 'replaced'
  | 'not_applicable';

export interface StatusMeta {
  label: string;
  /** Short label for dense cells (annual grid, chips). */
  short: string;
  tone: StatusTone;
  /** Whether the supplier has to do something. */
  actionRequired: boolean;
}

export const STATUS_META: Record<DisplayStatus, StatusMeta> = {
  approved: { label: 'Aprobado', short: 'Aprobado', tone: 'success', actionRequired: false },
  pending_approval: { label: 'En revisión', short: 'En revisión', tone: 'info', actionRequired: false },
  correction_requested: { label: 'Corrección solicitada', short: 'Corregir', tone: 'warning', actionRequired: true },
  rejected: { label: 'Rechazado', short: 'Rechazado', tone: 'danger', actionRequired: true },
  overdue: { label: 'Vencido', short: 'Vencido', tone: 'danger', actionRequired: true },
  missing: { label: 'Pendiente de entrega', short: 'Pendiente', tone: 'neutral', actionRequired: true },
  expired: { label: 'Expirado', short: 'Expirado', tone: 'danger', actionRequired: true },
  replaced: { label: 'Reemplazado', short: 'Reemplazado', tone: 'neutral', actionRequired: false },
  not_applicable: { label: 'No aplica', short: 'No aplica', tone: 'neutral', actionRequired: false },
};

export const TONE_CLASSES: Record<StatusTone, string> = {
  success: 'bg-green-50 text-green-800 ring-green-200',
  warning: 'bg-amber-50 text-amber-800 ring-amber-200',
  danger: 'bg-red-50 text-red-800 ring-red-200',
  info: 'bg-blue-50 text-blue-800 ring-blue-200',
  neutral: 'bg-slate-100 text-slate-700 ring-slate-200',
};

/** Maps raw database document statuses to a display status. */
export function toDisplayStatus(raw: string | null | undefined): DisplayStatus {
  switch (raw) {
    case 'approved':
    case 'fulfilled':
      return 'approved';
    case 'pending_approval':
      return 'pending_approval';
    case 'rejected':
      return 'rejected';
    case 'overdue':
      return 'overdue';
    case 'expired':
      return 'expired';
    case 'replaced':
    case 'reemplazado':
      return 'replaced';
    case 'waived':
      return 'not_applicable';
    default:
      return 'missing';
  }
}

export const statusBadgeClass = (status: DisplayStatus) =>
  `inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${TONE_CLASSES[STATUS_META[status].tone]}`;
